import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { SPORTS } from "../../constants/vars";
import useActiveProfile from "../common/hooks/useActiveProfile";
import { alphaFromHex } from "../../util/colors";
import useThemeColors from "../organization/portal/useThemeColors";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

/* ────────────────────────────────────────────────────────────────────────────
   GQL
──────────────────────────────────────────────────────────────────────────── */
export const MY_SPONSORS = gql`
    query MySponsorsForCampaign {
        sponsors(page: 1, pageSize: 100) {
            objects {
                id
                name
                isActive
                billingCountryCode
                billingCurrency
            }
        }
    }
`;

export const AD_PRICING_INPUTS = gql`
    query AdPricingInputs($zoneKeys: [String], $currency: String) {
        adZones {
            id
            key
            name
            description
        }
        adZoneRates(zoneKeys: $zoneKeys, currency: $currency) {
            id
            country
            currency
            dailyRate
            zone {
                key
            }
        }
    }
`;

export const MANAGED_ORGANIZATIONS_FOR_CAMPAIGN = gql`
    query ManagedOrganizationsForCampaign($profileId: Int!, $page: Int, $pageSize: Int) {
        organizations(profileId: $profileId, page: $page, pageSize: $pageSize) {
            objects {
                id
                name
                slug
                activeSubscription {
                    plan {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export const CREATE_CAMPAIGN_WITH_CREATIVE = gql`
    mutation CreateCampaignWithCreative(
        $campaign: CampaignPartInput!
        $creative: CreativePartInput!
    ) {
        createCampaignWithCreative(campaign: $campaign, creative: $creative) {
            success
            errors
            campaign {
                id
                name
                status
            }
            creative {
                id
            }
        }
    }
`;

export const UPDATE_DRAFT_CAMPAIGN = gql`
    mutation UpdateDraftCampaign(
        $campaign: DraftCampaignUpdateInput!
        $creative: DraftCreativeUpsertInput
    ) {
        updateDraftCampaign(campaign: $campaign, creative: $creative) {
            success
            errors
            campaign {
                id
                name
                status
            }
            creative {
                id
            }
        }
    }
`;

export const UPDATE_CAMPAIGN_TEXT = gql`
    mutation UpdateCampaignText($campaign: CampaignTextUpdateInput!) {
        updateCampaignText(campaign: $campaign) {
            success
            errors
            campaign {
                id
                name
                status
            }
            creative {
                id
            }
        }
    }
`;

/* ────────────────────────────────────────────────────────────────────────────
   Small UI primitives
──────────────────────────────────────────────────────────────────────────── */
function CollapsibleCard({
                             scopeId,
                             step,
                             title,
                             open,
                             onToggle,
                             children,
                             rightSlot,
                         }) {
    const { t } = useTranslation();
    const panelId = `${scopeId}-${String(title).replace(/\s+/g, "-").toLowerCase()}`;

    return (
        <div className="card kbola-card">
            <div className="kbola-card-header">
                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    {typeof step !== "undefined" ? (
                        <span className="kbola-step-badge">{step}</span>
                    ) : null}
                    <h3 className="kbola-card-title">{title}</h3>
                </div>

                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    {rightSlot}
                    <button
                        type="button"
                        className="btn btn-tool kbola-collapse-btn"
                        onClick={onToggle}
                        aria-expanded={open ? "true" : "false"}
                        aria-controls={panelId}
                        title={open ? t("campaignForm.collapse") : t("campaignForm.expand")}
                    >
                        <i className={`fas ${open ? "fa-minus" : "fa-plus"}`} />
                    </button>
                </div>
            </div>

            <div
                id={panelId}
                className={`kbola-collapse-body ${open ? "is-open" : "is-closed"}`}
            >
                <div className="card-body kbola-card-body">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, required, hint, children }) {
    return (
        <div className="form-group kbola-field">
            {label ? (
                <label className="kbola-label">
                    {label} {required ? <span className="kbola-required">*</span> : null}
                </label>
            ) : null}
            {children}
            {hint ? <small className="kbola-hint">{hint}</small> : null}
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────────
   Styled File Input
──────────────────────────────────────────────────────────────────────────── */
function FilePicker({
                        scopeId,
                        colors,
                        label,
                        hint,
                        accept = "image/*",
                        value,
                        previewUrl,
                        onChangeFile,
                        onClear,
                    }) {
    const { t } = useTranslation();
    const inputId = useMemo(
        () => `${scopeId}-file-${Math.random().toString(36).slice(2, 9)}`,
        [scopeId]
    );

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer?.files?.[0];
        if (f) onChangeFile(f);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div className="form-group kbola-field">
            {label ? <label className="kbola-label">{label}</label> : null}

            <input
                id={inputId}
                className="kbola-file-native"
                type="file"
                accept={accept}
                onChange={(e) => onChangeFile(e.target.files?.[0] || null)}
            />

            <div
                className="kbola-file-shell"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                role="group"
                aria-label={t("campaignForm.filePicker")}
            >
                <label htmlFor={inputId} className="kbola-file-btn">
                    <i className="fas fa-upload mr-2" />
                    {t("campaignForm.chooseFile")}
                </label>

                <div className="kbola-file-meta">
                    {value?.name ? (
                        <span className="kbola-file-name" title={value.name}>
                            {value.name}
                        </span>
                    ) : previewUrl ? (
                        <span className="kbola-file-name" title={t("campaignForm.existingImage")}>
                            {t("campaignForm.existingImageLower")}
                        </span>
                    ) : (
                        <span className="kbola-file-placeholder">{t("campaignForm.noFileSelected")}</span>
                    )}
                    <div className="kbola-file-subhint">
                        {t("campaignForm.dragDropHint")}
                    </div>
                </div>

                {value || previewUrl ? (
                    <button
                        type="button"
                        className="kbola-file-clear"
                        onClick={onClear}
                        title={t("campaignForm.remove")}
                    >
                        <i className="fas fa-times" />
                    </button>
                ) : null}
            </div>

            {hint ? <small className="kbola-hint">{hint}</small> : null}

            {previewUrl ? (
                <div className="kbola-file-preview">
                    <div className="kbola-file-thumb">
                        <img src={previewUrl} alt={t("campaignForm.logoPreview")} />
                    </div>
                    <div className="kbola-file-preview-text">
                        <div style={{ fontWeight: 900, color: colors.textOnSurface }}>
                            {t("campaignForm.preview")}
                        </div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>
                            {value?.name || t("campaignForm.existingImageLower")}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────────
   URL normalization
──────────────────────────────────────────────────────────────────────────── */
const API_BASE = process.env.REACT_APP_API_HOST;

const normalizeUrl = (u) => {
    if (!u) return null;
    if (/^(blob:|data:)/i.test(u)) return u;
    if (/^https?:\/\//i.test(u)) return u;

    const base = String(API_BASE || "").replace(/\/$/, "");
    if (!base) return u;

    if (u.startsWith("/")) return `${base}${u}`;
    return `${base}/${u}`;
};

const toIsoDate = (value) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const COUNTRY_OPTIONS_FALLBACK = [
    { code: "US", labelKey: "campaignForm.countryUnitedStates" },
    { code: "CA", labelKey: "campaignForm.countryCanada" },
];

const COUNTRY_LABEL = {
    US: "campaignForm.countryUnitedStates",
    CA: "campaignForm.countryCanada",
};

const PLATFORM_OPTIONS = [
    { value: "ios", labelKey: "campaignForm.platformOptions.ios" },
    { value: "android", labelKey: "campaignForm.platformOptions.android" },
];

const CAMPAIGN_TYPE_PAID_GLOBAL = "paid_global";
const CAMPAIGN_TYPE_ORGANIZATION = "organization";
const CAMPAIGN_TYPE_INTERNAL = "internal";
const EDITABLE_CAMPAIGN_STATUSES = ["running", "paused", "archived"];

const getAdZoneLabel = (t, zone, field) => {
    const fallback =
        field === "description"
            ? zone?.description || t("campaignForm.placementFallback")
            : zone?.name || zone?.key;

    return t(`campaignForm.adZones.${zone?.key}.${field}`, {}, fallback);
};

const isEnterpriseOrganization = (org) =>
    String(org?.activeSubscription?.plan?.name || "").trim().toLowerCase() === "enterprise";

const mergeOrganizationsById = (...organizationLists) => {
    const byId = new Map();

    organizationLists.flat().forEach((org) => {
        if (!org?.id) return;
        const existing = byId.get(String(org.id)) || {};
        byId.set(String(org.id), {
            ...existing,
            ...org,
            activeSubscription: org.activeSubscription || existing.activeSubscription,
        });
    });

    return Array.from(byId.values()).sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
    );
};

export default function CampaignForm({
                                         mode = "create",
                                         initialData,
                                         sponsorsFromParent,
                                         organizationsFromParent = [],
                                         onCancel,
                                         onSaved,
}) {
    const { t } = useTranslation();
    const { activeProfile, authData } = useActiveProfile();
    const navigate = useNavigate();
    const colors = useThemeColors();

    const scopeId = useMemo(
        () => `kbola-campaign-form-${Math.random().toString(36).slice(2, 9)}`,
        []
    );

    const primarySoft = alphaFromHex(colors.primary, 0.22);
    const primaryBorder = alphaFromHex(colors.primary, 0.35);
    const primaryFocus = alphaFromHex(colors.primary, 0.26);
    const primaryHover = alphaFromHex(colors.primary, 0.55);
    const secondaryBorder = alphaFromHex(colors.secondary, 0.35);
    const surfaceBorder = colors.border || "rgba(20,21,26,0.10)";

    /* ── Collapsible sections state ────────────────────────────────────────── */
    const [openSec, setOpenSec] = useState({
        basics: true,
        placement: false,
        targeting: false,
        schedule: false,
        creative: false,
    });

    const toggleSec = useCallback((key) => {
        setOpenSec((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    /* ── Form state ────────────────────────────────────────────────────────── */
    const [campaignName, setCampaignName] = useState("");
    const [sponsorId, setSponsorId] = useState("");
    const [zones, setZones] = useState([]);
    const [countries, setCountries] = useState(["US", "CA"]);
    const [platforms, setPlatforms] = useState(["ios", "android"]);
    const [sports, setSports] = useState(() =>
        mode === "create" ? SPORTS.map((sport) => sport.key) : []
    );
    const [targetOrganizationIds, setTargetOrganizationIds] = useState([]);
    const [campaignType, setCampaignType] = useState(CAMPAIGN_TYPE_PAID_GLOBAL);
    const [marketplacePromotionIncluded, setMarketplacePromotionIncluded] = useState(false);
    const [campaignStatus, setCampaignStatus] = useState("");
    const [currencyFallback, setCurrencyFallback] = useState("USD");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const organizationSelectionTouched = useRef(false);

    const [headline, setHeadline] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [ctaLabel, setCtaLabel] = useState("");
    const [ctaUrl, setCtaUrl] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    /* ── Hydrate fields in EDIT mode ───────────────────────────────────────── */
    useEffect(() => {
        if (mode !== "edit" || !initialData) return;

        const c = initialData.campaign || {};
        const t = c.targeting || {};

        setCampaignName(c.name || "");
        setSponsorId(c.sponsorId ? String(c.sponsorId) : "");
        setZones(c.zoneKeys || []);

        setStartDate(toIsoDate(c.startDate || c.startsAt));
        setEndDate(toIsoDate(c.endDate || c.endsAt));

        const cCountries =
            (Array.isArray(c.countries) && c.countries.length > 0
                ? c.countries
                : Array.isArray(t.countries)
                    ? t.countries
                    : ["US", "CA"]) || ["US", "CA"];
        setCountries(cCountries.map((x) => String(x).toUpperCase()));

        setPlatforms(
            Array.isArray(t.platforms) && t.platforms.length
                ? t.platforms
                : ["ios", "android"]
        );
        setSports(
            Array.isArray(t.sports) && t.sports.length
                ? t.sports
                : c.marketplacePromotionIncluded
                    ? SPORTS.map((sport) => sport.key)
                    : []
        );
        setTargetOrganizationIds(
            Array.isArray(c.targetOrganizationIds) ? c.targetOrganizationIds : []
        );
        setCampaignType(c.campaignType || CAMPAIGN_TYPE_PAID_GLOBAL);
        setMarketplacePromotionIncluded(!!c.marketplacePromotionIncluded);
        setCampaignStatus(String(c.status || "").toLowerCase());
        setCurrencyFallback(String(c.billingCurrency || "USD").toUpperCase());

        const cr = initialData.creative || {};
        setHeadline(cr.title || "");
        setBodyText(cr.description || "");
        setCtaLabel(cr.ctaLabel || "");
        setCtaUrl(cr.ctaUrl || "");
        if (cr.imageUrl) setLogoPreviewUrl(normalizeUrl(cr.imageUrl));
    }, [mode, initialData]);

    /* ── Sponsors ──────────────────────────────────────────────────────────── */
    const skipSponsorsQuery =
        Array.isArray(sponsorsFromParent) && sponsorsFromParent.length > 0;

    const { data: sponsorData } = useQuery(MY_SPONSORS, {
        fetchPolicy: "cache-and-network",
        skip: skipSponsorsQuery,
    });

    const sponsors = useMemo(() => {
        if (skipSponsorsQuery) return sponsorsFromParent;
        return sponsorData?.sponsors?.objects ?? [];
    }, [skipSponsorsQuery, sponsorsFromParent, sponsorData]);

    const onlySponsorId = useMemo(() => {
        if (mode !== "create") return "";
        if (!Array.isArray(sponsors) || sponsors.length !== 1) return "";
        return String(sponsors[0]?.id || "");
    }, [mode, sponsors]);

    useEffect(() => {
        if (!sponsorId && onlySponsorId) setSponsorId(onlySponsorId);
    }, [sponsorId, onlySponsorId]);

    const selectedSponsor = useMemo(
        () => sponsors.find((s) => String(s.id) === String(sponsorId)),
        [sponsors, sponsorId]
    );

    const { data: managedOrganizationsData, loading: managedOrganizationsLoading } = useQuery(
        MANAGED_ORGANIZATIONS_FOR_CAMPAIGN,
        {
            variables: {
                profileId: Number(activeProfile?.id || 0),
                page: 1,
                pageSize: 100,
            },
            skip: !activeProfile?.id,
            fetchPolicy: "cache-and-network",
        }
    );

    const managedOrganizations = useMemo(
        () =>
            mergeOrganizationsById(
                managedOrganizationsData?.organizations?.objects || [],
                organizationsFromParent || []
            ),
        [managedOrganizationsData, organizationsFromParent]
    );

    const hasEnterpriseCampaignAccess = useMemo(
        () => managedOrganizations.some(isEnterpriseOrganization),
        [managedOrganizations]
    );

    const targetableOrganizations = useMemo(
        () => (hasEnterpriseCampaignAccess ? managedOrganizations : []),
        [hasEnterpriseCampaignAccess, managedOrganizations]
    );

    const isStaffUser = authData?.user?.isStaff === true;
    const isInternalCampaign = campaignType === CAMPAIGN_TYPE_INTERNAL;
    const isMarketplacePromotionIncluded = mode === "edit" && marketplacePromotionIncluded;
    const normalizedStatus = String(initialData?.campaign?.status || "").toLowerCase();
    const isTextOnlyEdit = mode === "edit" && normalizedStatus && normalizedStatus !== "draft";

    useEffect(() => {
        if (managedOrganizationsLoading) return;
        if (isInternalCampaign) return;

        if (!hasEnterpriseCampaignAccess) {
            setTargetOrganizationIds([]);
            return;
        }

        const targetableIds = targetableOrganizations.map((org) => org.id).filter(Boolean);
        const allowed = new Set(targetableIds.map((id) => String(id)));
        setTargetOrganizationIds((prev) => {
            if (mode === "create" && !organizationSelectionTouched.current) {
                return targetableIds;
            }

            return prev.filter((id) => allowed.has(String(id)));
        });
    }, [hasEnterpriseCampaignAccess, isInternalCampaign, managedOrganizationsLoading, mode, targetableOrganizations]);

    const effectiveCurrency = useMemo(() => {
        return String(
            selectedSponsor?.billingCurrency ||
            currencyFallback ||
            activeProfile?.currency ||
            "USD"
        ).toUpperCase();
    }, [selectedSponsor, currencyFallback, activeProfile?.currency]);

    /* ── Pricing inputs (zones + rates) ───────────────────────────────────── */
    const { data: pricingData } = useQuery(AD_PRICING_INPUTS, {
        variables: {
            zoneKeys: zones.length ? zones : null,
            currency: effectiveCurrency,
        },
        fetchPolicy: "cache-and-network",
    });

    const zoneOptions = useMemo(() => pricingData?.adZones ?? [], [pricingData]);
    const zoneRates = useMemo(() => pricingData?.adZoneRates ?? [], [pricingData]);
    const placementDefaultsApplied = useRef(false);

    useEffect(() => {
        if (mode !== "create" || placementDefaultsApplied.current || !zoneOptions.length) {
            return;
        }

        setZones(zoneOptions.map((zone) => zone.key).filter(Boolean));
        placementDefaultsApplied.current = true;
    }, [mode, zoneOptions]);

    const dynamicCountryOptions = useMemo(() => {
        const codes = new Set();
        zoneRates.forEach((r) => {
            const code = String(r?.country || "").toUpperCase();
            if (code) codes.add(code);
        });

        if (!codes.size) {
            return COUNTRY_OPTIONS_FALLBACK.map((country) => ({
                code: country.code,
                label: t(country.labelKey),
            }));
        }

        return Array.from(codes).map((code) => ({
            code,
            label: COUNTRY_LABEL[code] ? t(COUNTRY_LABEL[code]) : code,
        }));
    }, [t, zoneRates]);

    useEffect(() => {
        if (!dynamicCountryOptions.length) return;
        const allowed = new Set(dynamicCountryOptions.map((x) => x.code));
        setCountries((prev) => {
            const filtered = prev.filter((c) => allowed.has(c));
            return filtered.length ? filtered : [dynamicCountryOptions[0].code];
        });
    }, [dynamicCountryOptions]);

    /* ── Mutations ─────────────────────────────────────────────────────────── */
    const [createMutation, { loading: creating }] = useMutation(
        CREATE_CAMPAIGN_WITH_CREATIVE
    );
    const [updateMutation, { loading: updating }] = useMutation(
        UPDATE_DRAFT_CAMPAIGN
    );
    const [updateTextMutation, { loading: updatingText }] = useMutation(
        UPDATE_CAMPAIGN_TEXT
    );

    /* ── Toggles ───────────────────────────────────────────────────────────── */
    const togglePlatform = (plat) => {
        setPlatforms((prev) =>
            prev.includes(plat) ? prev.filter((p) => p !== plat) : [...prev, plat]
        );
    };
    const toggleZone = (zoneKey) => {
        setZones((prev) =>
            prev.includes(zoneKey) ? prev.filter((z) => z !== zoneKey) : [...prev, zoneKey]
        );
    };
    const toggleSport = (sportKey) => {
        setSports((prev) =>
            prev.includes(sportKey) ? prev.filter((s) => s !== sportKey) : [...prev, sportKey]
        );
    };
    const toggleCountry = (code) => {
        setCountries((prev) =>
            prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
        );
    };
    const toggleOrganization = (id) => {
        organizationSelectionTouched.current = true;
        setTargetOrganizationIds((prev) =>
            prev.some((x) => String(x) === String(id))
                ? prev.filter((x) => String(x) !== String(id))
                : [...prev, id]
        );
    };

    const handleCampaignScopeChange = (nextType) => {
        setCampaignType(nextType);
    };

    /* ── File preview ──────────────────────────────────────────────────────── */
    useEffect(() => {
        if (!logoFile) {
            if (mode === "create") setLogoPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile, mode]);

    const clearLogo = useCallback(() => {
        setLogoFile(null);
        if (mode === "create") setLogoPreviewUrl(null);
    }, [mode]);

    /* ── Pricing ───────────────────────────────────────────────────────────── */
    const isOrganizationCampaign = !isInternalCampaign && hasEnterpriseCampaignAccess && targetOrganizationIds.length > 0;
    const isNoPaymentCampaign = isInternalCampaign || isOrganizationCampaign || isMarketplacePromotionIncluded;
    const effectiveCampaignType = isInternalCampaign
        ? CAMPAIGN_TYPE_INTERNAL
        : isOrganizationCampaign
            ? CAMPAIGN_TYPE_ORGANIZATION
            : CAMPAIGN_TYPE_PAID_GLOBAL;

    const pricing = useMemo(() => {
        if (isNoPaymentCampaign) {
            return { total: "0.00", missing: [], days: 0 };
        }

        if (!startDate || !endDate || zones.length === 0 || countries.length === 0) {
            return { total: "0.00", missing: [], days: 0 };
        }

        const d1 = new Date(startDate);
        const d2 = new Date(endDate);
        if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) {
            return { total: "0.00", missing: [], days: 0 };
        }

        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        const days = Math.max(1, Math.round((d2 - d1) / MS_PER_DAY) + 1);
        const cur = effectiveCurrency;

        const ratesMap = new Map();
        zoneRates.forEach((r) => {
            const zoneKey = r?.zone?.key;
            const country = String(r?.country || "").toUpperCase();
            const currency = String(r?.currency || "").toUpperCase();
            const dailyRate = Number(r?.dailyRate);
            if (!zoneKey || !country || !currency || Number.isNaN(dailyRate)) return;
            ratesMap.set(`${zoneKey}|${country}|${currency}`, dailyRate);
        });

        let dailyTotal = 0;
        const missing = [];
        zones.forEach((zoneKey) => {
            countries.forEach((country) => {
                const rate = ratesMap.get(`${zoneKey}|${country}|${cur}`);
                if (typeof rate !== "number") {
                    missing.push({ zoneKey, country, currency: cur });
                } else {
                    dailyTotal += rate;
                }
            });
        });

        if (missing.length) return { total: "0.00", missing, days };

        const amount = dailyTotal * days;
        const finalAmount = Math.max(amount, 5); // mirrors backend minimum 500 cents
        return { total: Number(finalAmount).toFixed(2), missing, days };
    }, [isNoPaymentCampaign, startDate, endDate, zones, countries, zoneRates, effectiveCurrency]);

    const estimatedTotal = pricing.total;
    const hasMissingRates = !isNoPaymentCampaign && pricing.missing.length > 0;
    const hasCreativeImage = !!logoFile || !!logoPreviewUrl;

    const missingRateText = useMemo(() => {
        if (!hasMissingRates) return "";
        const list = pricing.missing.slice(0, 3).map((m) => `${m.zoneKey}/${m.country}`);
        const remainingCount = pricing.missing.length - 3;
        const suffix = remainingCount > 0 ? ` ${t("campaignForm.moreItems", { count: remainingCount })}` : "";
        return `${list.join(", ")}${suffix}`;
    }, [hasMissingRates, pricing.missing, t]);

    /* ── Validation ────────────────────────────────────────────────────────── */
    const buildTargeting = () => ({
        countries,
        platforms,
        sports,
        dateRange: { start: startDate || null, end: endDate || null },
    });

    const buildCampaignPayload = (status = "draft") => ({
        name: campaignName.trim(),
        sponsorId,
        campaignType: effectiveCampaignType,
        zoneKeys: zones,
        countries,
        targetOrganizationIds: isOrganizationCampaign ? targetOrganizationIds : [],
        billingCurrency: effectiveCurrency.toLowerCase(),
        targeting: buildTargeting(),
        startDate: startDate || null,
        endDate: endDate || null,
        status,
    });

    const isValid = () => {
        if (isTextOnlyEdit) {
            return campaignName.trim() && headline.trim() && bodyText.trim() && ctaLabel.trim() && ctaUrl.trim();
        }

        return (
            campaignName.trim() &&
            sponsorId &&
            zones.length > 0 &&
            countries.length > 0 &&
            platforms.length > 0 &&
            startDate &&
            endDate &&
            headline.trim() &&
            bodyText.trim() &&
            ctaLabel.trim() &&
            ctaUrl.trim() &&
            hasCreativeImage &&
            !hasMissingRates
        );
    };

    /* ── Handlers ─────────────────────────────────────────────────────────── */
    const handleCreateDraft = async () => {
        if (!campaignName.trim() || !sponsorId || zones.length === 0) {
            toast.error(t("campaignForm.toast.enterNameSponsorPlacement"));
            return;
        }

        if (hasMissingRates) {
            toast.error(t("campaignForm.toast.missingRates"));
            return;
        }

        if (!hasCreativeImage) {
            toast.error(t("campaignForm.toast.provideImage"));
            return;
        }

        const campaign = buildCampaignPayload("draft");

        const creative = {
            adType: "card",
            title: headline || "",
            description: bodyText || "",
            ctaLabel: ctaLabel || "",
            ctaUrl: ctaUrl || "",
            weight: 1,
            isActive: true,
            ...(logoFile
                ? { imageFile: logoFile }
                : logoPreviewUrl
                    ? { imageUrl: logoPreviewUrl }
                    : {}),
        };

        try {
            const { data } = await createMutation({ variables: { campaign, creative } });
            const res = data?.createCampaignWithCreative;
            if (!res?.success) {
                const errs = Array.isArray(res?.errors)
                    ? res.errors.join(", ")
                    : res?.errors || t("campaignForm.toast.unknownError");
                toast.error(`${t("campaignForm.toast.couldNotSaveDraft")} ${errs}`);
                return;
            }
            toast.success(t("campaignForm.toast.draftSaved"));
            onSaved && onSaved(res?.campaign);
        } catch (e) {
            toast.error(getLocalizedGraphQLError(e, t, "campaignForm.toast.somethingWentWrongSaving"));
        }
    };

    const handleUpdateDraft = async () => {
        const campaignId = initialData?.campaign?.id;
        if (!campaignId) {
            toast.error(t("campaignForm.toast.missingCampaignId"));
            return;
        }
        if (!isValid()) {
            toast.error(t("campaignForm.toast.completeRequiredFields"));
            return;
        }

        if (isTextOnlyEdit) {
            const patch = {
                id: campaignId,
                name: campaignName.trim(),
                status: EDITABLE_CAMPAIGN_STATUSES.includes(campaignStatus) ? campaignStatus : undefined,
                creativeId: initialData?.creative?.id,
                title: headline || "",
                description: bodyText || "",
                ctaLabel: ctaLabel || "",
                ctaUrl: ctaUrl || "",
            };

            try {
                const { data } = await updateTextMutation({ variables: { campaign: patch } });
                const res = data?.updateCampaignText;
                if (!res?.success) {
                    const errs = Array.isArray(res?.errors)
                        ? res.errors.join(", ")
                        : res?.errors || t("campaignForm.toast.unknownError");
                    toast.error(`${t("campaignForm.toast.updateFailed")} ${errs}`);
                    return;
                }
                toast.success(t("campaignForm.toast.campaignTextUpdated", {}, "Campaign text updated!"));
                onSaved && onSaved(res?.campaign);
            } catch (e) {
                toast.error(getLocalizedGraphQLError(e, t, "campaignForm.toast.somethingWentWrongUpdating"));
            }
            return;
        }

        const patch = {
            id: campaignId,
            name: campaignName.trim(),
            campaignType: effectiveCampaignType,
            zoneKeys: zones,
            countries,
            targetOrganizationIds: isOrganizationCampaign ? targetOrganizationIds : [],
            billingCurrency: effectiveCurrency.toLowerCase(),
            targeting: buildTargeting(),
            startDate: startDate || null,
            endDate: endDate || null,
        };

        let creative = null;
        if (logoFile || headline || bodyText || ctaLabel || ctaUrl) {
            creative = {
                id: initialData?.creative?.id,
                adType: "card",
                title: headline || "",
                description: bodyText || "",
                ctaLabel: ctaLabel || "",
                ctaUrl: ctaUrl || "",
                weight: 1,
                isActive: true,
                ...(logoFile ? { imageFile: logoFile } : {}),
            };
        }

        try {
            const { data } = await updateMutation({ variables: { campaign: patch, creative } });
            const res = data?.updateDraftCampaign;
            if (!res?.success) {
                const errs = Array.isArray(res?.errors)
                    ? res.errors.join(", ")
                    : res?.errors || t("campaignForm.toast.unknownError");
                toast.error(`${t("campaignForm.toast.updateFailed")} ${errs}`);
                return;
            }
            toast.success(t("campaignForm.toast.draftUpdated"));
            onSaved && onSaved(res?.campaign);
        } catch (e) {
            toast.error(getLocalizedGraphQLError(e, t, "campaignForm.toast.somethingWentWrongUpdating"));
        }
    };

    async function saveAndGoToReview() {
        try {
            if (isTextOnlyEdit) {
                await handleUpdateDraft();
                return;
            }

            if (mode === "create") {
                const campaign = buildCampaignPayload("draft");
                const creative = {
                    adType: "card",
                    title: headline || "",
                    description: bodyText || "",
                    ctaLabel: ctaLabel || "",
                    ctaUrl: ctaUrl || "",
                    weight: 1,
                    isActive: true,
                    ...(logoFile
                        ? { imageFile: logoFile }
                        : logoPreviewUrl
                            ? { imageUrl: logoPreviewUrl }
                            : {}),
                };

                const { data } = await createMutation({ variables: { campaign, creative } });
                const res = data?.createCampaignWithCreative;
                if (!res?.success || !res?.campaign?.id) {
                    const errs = Array.isArray(res?.errors)
                        ? res.errors.join(", ")
                    : res?.errors || t("campaignForm.toast.unknownError");
                toast.error(`${t("campaignForm.toast.couldNotSaveDraft")} ${errs}`);
                    return;
                }
                navigate(`/advertise/campaign/${res.campaign.id}/review`);
                return;
            }

            const campaignId = initialData?.campaign?.id;
            const patch = {
                id: campaignId,
                name: campaignName.trim(),
                campaignType: effectiveCampaignType,
                zoneKeys: zones,
                countries,
                targetOrganizationIds: isOrganizationCampaign ? targetOrganizationIds : [],
                billingCurrency: effectiveCurrency.toLowerCase(),
                targeting: buildTargeting(),
                startDate: startDate || null,
                endDate: endDate || null,
            };

            let creative = null;
            if (logoFile || headline || bodyText || ctaLabel || ctaUrl) {
                creative = {
                    id: initialData?.creative?.id,
                    adType: "card",
                    title: headline || "",
                    description: bodyText || "",
                    ctaLabel: ctaLabel || "",
                    ctaUrl: ctaUrl || "",
                    weight: 1,
                    isActive: true,
                    ...(logoFile ? { imageFile: logoFile } : {}),
                };
            }

            const { data } = await updateMutation({ variables: { campaign: patch, creative } });
            const res = data?.updateDraftCampaign;
            if (!res?.success) {
                const errs = Array.isArray(res?.errors)
                    ? res.errors.join(", ")
                    : res?.errors || t("campaignForm.toast.unknownError");
                toast.error(`${t("campaignForm.toast.updateFailed")} ${errs}`);
                return;
            }

            navigate(`/advertise/campaign/${patch.id}/review`);
        } catch (e) {
            toast.error(getLocalizedGraphQLError(e, t, "campaignForm.toast.couldNotProceedToReview"));
        }
    }

    const submitDisabled = (mode === "create" ? creating : updating || updatingText) || !isValid();

    const selectedSponsorName =
        sponsors.find((s) => String(s.id) === String(sponsorId))?.name || "—";

    const getSportLabel = (sportKey) => {
        const sport = SPORTS.find((s) => s.key === sportKey);
        return t(`signUp.form.sports.${sportKey}`, {}, sport?.value || sportKey);
    };

    const logoSrc = normalizeUrl(logoPreviewUrl);

    return (
        <div
            id={scopeId}
            className="content"
            style={{
                paddingBottom: 92,
            }}
        >
            <style>{`
        #${scopeId} { font-family: Poppins, system-ui, -apple-system, Segoe UI, Roboto, Arial; }

        #${scopeId} .kbola-card{
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 18px 40px rgba(0,0,0,0.06);
          margin-bottom: 18px;
          background: ${colors.surface};
        }
        #${scopeId} .kbola-card-body{ padding: 16px; background: ${colors.white}; }

        #${scopeId} .kbola-card-header{
          position: relative;
          background: linear-gradient(135deg, ${colors.primary} 35%, ${colors.accent} 80%, ${colors.secondary} 100%);
          color: ${colors.textOnPrimary};
          padding: 14px 16px;
          display:flex;
          align-items:center;
          justify-content:space-between;
        }
        #${scopeId} .kbola-card-header::after{
          content:"";
          position:absolute;
          inset:0;
          background: radial-gradient(900px 240px at 10% 0%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 60%);
          pointer-events:none;
          opacity: .9;
        }
        #${scopeId} .kbola-card-header *{ position:relative; z-index:1; }

        #${scopeId} .kbola-card-title{
          margin:0;
          font-size:16px;
          font-weight:900;
          line-height:1.1;
          color:${colors.textOnPrimary};
        }
        #${scopeId} .kbola-step-badge{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:26px;height:26px;
          border-radius:9px;
          background:${colors.secondary};
          color:${colors.textOnSecondary};
          font-weight:900;
          font-size:13px;
          box-shadow: 0 10px 22px rgba(0,0,0,0.12);
        }

        #${scopeId} .kbola-collapse-btn{
          color:${colors.textOnPrimary};
        }

        #${scopeId} .kbola-collapse-body.is-closed{ display:none; }
        #${scopeId} .kbola-collapse-body.is-open{ display:block; }

        #${scopeId} .kbola-label{
          font-weight:900;
          color: ${colors.woodsmoke} !important;
          font-size: 13px;
          margin-bottom: 6px;
        }
        #${scopeId} .kbola-required{ color:#b10000; }
        #${scopeId} .kbola-hint{
          color:${colors.textMuted};
          font-size: 12px;
          margin-top: 6px;
          display:block;
        }

        #${scopeId} .kbola-input{
          border-radius: 14px !important;
          border: 1.5px solid ${surfaceBorder} !important;
          padding: 11px 12px !important;
          height: auto !important;
          font-weight: 700;
          color: ${colors.woodsmoke};
          box-shadow: none !important;
        }
        #${scopeId} .kbola-input::placeholder{
          color: ${colors.muted};
          font-weight: 600;
        }

        #${scopeId} textarea.kbola-input{ resize: vertical; }

        #${scopeId} .kbola-check-tile{
          display:flex;
          align-items:center;
          gap:10px;
          width:100%;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid ${surfaceBorder};
          cursor:pointer;
          transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
          user-select:none;
        }
        #${scopeId} .kbola-check-tile:hover{
          border-color: ${primaryHover};
          box-shadow: 0 10px 22px rgba(0,0,0,0.06);
          transform: translateY(-1px);
        }
        #${scopeId} .kbola-check{
          appearance:none;
          -webkit-appearance:none;
          width:18px;height:18px;
          border-radius:6px;
          border: 1.5px solid ${alphaFromHex(colors.textOnSurface, 0.28)};
          background: ${alphaFromHex(colors.secondary, 0.95)};
          display:grid;
          place-items:center;
          flex:0 0 18px;
          transition: background .12s ease, border-color .12s ease, box-shadow .12s ease;
        }
        #${scopeId} .kbola-check:focus-visible{
          outline:none;
          box-shadow: 0 0 0 3px ${primaryFocus};
        }
        #${scopeId} .kbola-check:checked{
          background:${colors.primary};
          border-color:${colors.primary};
        }
        #${scopeId} .kbola-check:checked::after{
          content:"";
          width:9px;height:9px;
          border-right:2px solid ${colors.textOnPrimary};
          border-bottom:2px solid ${colors.textOnPrimary};
          transform: rotate(45deg);
          margin-top:-1px;
        }
        #${scopeId} .kbola-check-text{
          font-weight:900;
          color:${colors.woodsmoke};
          font-size:13px;
          line-height:1.2;
        }
        #${scopeId} .kbola-check-sub{
          font-size:12px;
          color:${colors.textMuted};
          margin-top:2px;
          line-height:1.2;
        }
        #${scopeId} .kbola-pill{
          border-radius: 999px;
          padding: 8px 12px;
          width: auto !important;
          color: ${colors.textOnPrimary};
          background: ${colors.white};
        }

        #${scopeId} .btn-kbola-canary{
          background:${colors.primary};
          border-color:${colors.primary};
          color:${colors.textOnPrimary};
          font-weight:900;
        }
        #${scopeId} .btn-kbola-canary:hover{
          background:${colors.secondary};
          border-color:${colors.secondary};
          color:${colors.textOnSecondary};
        }

        #${scopeId} .kbola-file-native{
          position:absolute;
          width:1px;
          height:1px;
          padding:0;
          margin:-1px;
          overflow:hidden;
          clip: rect(0,0,0,0);
          white-space:nowrap;
          border:0;
        }
        #${scopeId} .kbola-file-shell{
          display:flex;
          align-items:center;
          gap:10px;
          border-radius: 14px;
          border: 1.5px dashed ${surfaceBorder};
          background: ${alphaFromHex(colors.surface, 0.85)};
          padding: 10px 10px;
          transition: border-color .12s ease, box-shadow .12s ease, transform .12s ease;
        }
        #${scopeId} .kbola-file-shell:hover{
          border-color: ${primaryHover};
          box-shadow: 0 12px 26px rgba(0,0,0,0.06);
          transform: translateY(-1px);
        }
        #${scopeId} .kbola-file-shell:focus-within{
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px ${primaryFocus};
        }
        #${scopeId} .kbola-file-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          padding: 10px 12px;
          border-radius: 12px;
          background: ${colors.secondary};
          color: ${colors.textOnSecondary};
          font-weight: 900;
          cursor:pointer;
          margin:0;
          user-select:none;
          white-space:nowrap;
          border: 1px solid ${secondaryBorder};
        }
        #${scopeId} .kbola-file-meta{
          flex:1;
          min-width: 0;
        }
        #${scopeId} .kbola-file-name{
          display:inline-block;
          max-width: 100%;
          overflow:hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 800;
          color:${colors.textOnSurface};
          background: ${primarySoft};
          border: 1px solid ${primaryBorder};
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
        }
        #${scopeId} .kbola-file-placeholder{
          color: ${colors.textSoft};
          font-weight: 700;
          font-size: 12px;
        }
        #${scopeId} .kbola-file-subhint{
          margin-top: 4px;
          font-size: 11px;
          color: ${colors.textSoft};
          font-weight: 700;
        }
        #${scopeId} .kbola-file-clear{
          width: 38px;
          height: 38px;
          border-radius: 12px;
          border: 1px solid ${surfaceBorder};
          background: ${alphaFromHex(colors.surface, 0.9)};
          color: ${colors.textOnSurface};
          display:flex;
          align-items:center;
          justify-content:center;
          cursor:pointer;
        }
        #${scopeId} .kbola-file-preview{
          display:flex;
          align-items:center;
          gap:12px;
          margin-top: 10px;
        }
        #${scopeId} .kbola-file-thumb{
          width: 72px;
          height: 72px;
          border-radius: 14px;
          overflow:hidden;
          border: 1px solid ${surfaceBorder};
          box-shadow: 0 12px 24px rgba(0,0,0,0.06);
          background:${colors.surface};
        }
        #${scopeId} .kbola-file-thumb img{
          width:100%;
          height:100%;
          object-fit: cover;
          display:block;
        }
      `}</style>

            <div className="row">
                {/* LEFT */}
                <div className="col-12 col-lg-8">
                    <CollapsibleCard
                        scopeId={scopeId}
                        step={1}
                        title={t("campaignForm.basics")}
                        open={openSec.basics}
                        onToggle={() => toggleSec("basics")}
                    >
                        {isTextOnlyEdit ? (
                            <div className="alert alert-info" style={{ fontSize: 12 }}>
                                {t(
                                    "campaignForm.activeTextOnlyHint",
                                    {},
                                    "This campaign is already active or scheduled. You can correct the campaign name, headline, description, CTA label, and CTA URL without changing billing, dates, audience, or artwork."
                                )}
                            </div>
                        ) : null}

                        <Field label={t("campaignForm.campaignName")} required>
                            <input
                                className="form-control kbola-input"
                                placeholder={t("campaignForm.campaignNamePlaceholder")}
                                value={campaignName}
                                onChange={(e) => setCampaignName(e.target.value)}
                            />
                        </Field>

                        <Field label={t("campaignForm.sponsorBrand")} required>
                            <select
                                className="form-control kbola-input"
                                value={sponsorId}
                                onChange={(e) => setSponsorId(e.target.value)}
                                disabled={isTextOnlyEdit}
                            >
                                <option value="">{t("campaignForm.selectSponsor")}</option>
                                {sponsors.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name} {s.isActive ? "" : t("campaignForm.inactive")}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field
                            label={t("campaignForm.billingCurrency")}
                            required
                            hint={t("campaignForm.billingCurrencyHint")}
                        >
                            <input
                                className="form-control kbola-input"
                                value={effectiveCurrency}
                                readOnly
                            />
                        </Field>

                        {isTextOnlyEdit ? (
                            <Field
                                label={t("campaignForm.status", {}, "Status")}
                                required
                                hint={t(
                                    "campaignForm.statusEditHint",
                                    {},
                                    "Only running, paused, and archived are available after activation."
                                )}
                            >
                                <select
                                    className="form-control kbola-input"
                                    value={campaignStatus}
                                    onChange={(e) => setCampaignStatus(e.target.value)}
                                >
                                    {EDITABLE_CAMPAIGN_STATUSES.map((status) => (
                                        <option key={status} value={status}>
                                            {t(`campaigns.status.${status}`, {}, status)}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        ) : null}
                    </CollapsibleCard>

                    {!isTextOnlyEdit ? (
                        <CollapsibleCard
                            scopeId={scopeId}
                            step={2}
                            title={t("campaignForm.placement")}
                            open={openSec.placement}
                            onToggle={() => toggleSec("placement")}
                        >
                        <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
                            {t("campaignForm.placementHint")}
                        </div>

                        <div className="row">
                            {zoneOptions.map((zone) => {
                                const zoneName = getAdZoneLabel(t, zone, "name");
                                const zoneDescription = getAdZoneLabel(t, zone, "description");

                                return (
                                    <div className="col-12 col-md-6 mb-2" key={zone.key}>
                                        <label className="kbola-check-tile">
                                            <input
                                                className="kbola-check"
                                                type="checkbox"
                                                checked={zones.includes(zone.key)}
                                                onChange={() => toggleZone(zone.key)}
                                            />
                                            <div>
                                                <div className="kbola-check-text">{zoneName}</div>
                                                <div className="kbola-check-sub">{zoneDescription}</div>
                                            </div>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        </CollapsibleCard>
                    ) : null}

                    {!isTextOnlyEdit ? (
                        <CollapsibleCard
                            scopeId={scopeId}
                            step={3}
                            title={t("campaignForm.targeting")}
                            open={openSec.targeting}
                            onToggle={() => toggleSec("targeting")}
                        >
                        <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
                            {t("campaignForm.targetingHint")}
                        </div>

                        <div className="form-group">
                            <label className="kbola-label">
                                {t("campaignForm.countries")} <span className="kbola-required">*</span>
                            </label>
                            <div className="row">
                                {dynamicCountryOptions.map((c) => (
                                    <div className="col-12 col-md-6 mb-2" key={c.code}>
                                        <label className="kbola-check-tile">
                                            <input
                                                className="kbola-check"
                                                type="checkbox"
                                                checked={countries.includes(c.code)}
                                                onChange={() => toggleCountry(c.code)}
                                            />
                                            <div>
                                                <div className="kbola-check-text">{c.label}</div>
                                                <div className="kbola-check-sub">{t("campaignForm.geoTargeting")}</div>
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="kbola-label">
                                {t("campaignForm.platforms")} <span className="kbola-required">*</span>
                            </label>
                            <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                                {PLATFORM_OPTIONS.map((platform) => (
                                    <label key={platform.value} className="kbola-check-tile kbola-pill">
                                        <input
                                            className="kbola-check"
                                            type="checkbox"
                                            checked={platforms.includes(platform.value)}
                                            onChange={() => togglePlatform(platform.value)}
                                        />
                                        <div className="kbola-check-text">{t(platform.labelKey)}</div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="kbola-label">{t("campaignForm.sportsAudienceFocus")}</label>
                            <div className="row">
                                {SPORTS.map((s) => (
                                    <div className="col-12 col-md-6 mb-2" key={s.key}>
                                        <label className="kbola-check-tile">
                                            <input
                                                className="kbola-check"
                                                type="checkbox"
                                                checked={sports.includes(s.key)}
                                                onChange={() => toggleSport(s.key)}
                                            />
                                            <div>
                                                <div className="kbola-check-text">
                                                    {t(`signUp.form.sports.${s.key}`, {}, s.value)}
                                                </div>
                                                <div className="kbola-check-sub">{t("campaignForm.audienceSignal")}</div>
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <small className="kbola-hint">
                                {t("campaignForm.allSportsUncheckedHint")}
                            </small>
                        </div>

                        {isStaffUser ? (
                            <div className="form-group">
                                <label className="kbola-label">
                                    {t("campaignForm.campaignReach", {}, "Campaign reach")}
                                </label>
                                <div className="row">
                                    <div className="col-12 col-md-6 mb-2">
                                        <label className="kbola-check-tile">
                                            <input
                                                className="kbola-check"
                                                type="radio"
                                                name={`${scopeId}-campaign-reach`}
                                                checked={!isInternalCampaign}
                                                onChange={() => handleCampaignScopeChange(CAMPAIGN_TYPE_PAID_GLOBAL)}
                                            />
                                            <div>
                                                <div className="kbola-check-text">
                                                    {t("campaignForm.standardCampaign", {}, "Standard sponsor or league campaign")}
                                                </div>
                                                <div className="kbola-check-sub">
                                                    {t("campaignForm.standardCampaignHint", {}, "Uses normal paid or Enterprise organization rules.")}
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="col-12 col-md-6 mb-2">
                                        <label className="kbola-check-tile">
                                            <input
                                                className="kbola-check"
                                                type="radio"
                                                name={`${scopeId}-campaign-reach`}
                                                checked={isInternalCampaign}
                                                onChange={() => handleCampaignScopeChange(CAMPAIGN_TYPE_INTERNAL)}
                                            />
                                            <div>
                                                <div className="kbola-check-text">
                                                    {t("campaignForm.internalCampaign", {}, "Internal AngeonX Sports campaign")}
                                                </div>
                                                <div className="kbola-check-sub">
                                                    {t("campaignForm.internalCampaignHint", {}, "Visible to every eligible user, including Enterprise organizations.")}
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {isInternalCampaign ? (
                            <div className="alert alert-success mb-0" style={{ fontSize: 12 }}>
                                {t(
                                    "campaignForm.internalCampaignReachHint",
                                    {},
                                    "This internal campaign can reach users across all organizations. Organization targeting is not used."
                                )}
                            </div>
                        ) : hasEnterpriseCampaignAccess ? (
                            <div className="form-group mb-0">
                                <label className="kbola-label">{t("campaignForm.specificOrganizationsOptional")}</label>
                                <small className="kbola-hint mb-2">
                                    {t(
                                        "campaignForm.enterpriseOrganizationTargetingHint",
                                        {},
                                        "Enterprise league managers can target any managed organization. Leave empty for a paid global sponsor campaign."
                                    )}
                                </small>
                                <div className="row">
                                    {targetableOrganizations.map((org) => (
                                        <div className="col-12 col-md-6 mb-2" key={org.id}>
                                            <label className="kbola-check-tile">
                                                <input
                                                    className="kbola-check"
                                                    type="checkbox"
                                                    checked={targetOrganizationIds.some((id) => String(id) === String(org.id))}
                                                    onChange={() => toggleOrganization(org.id)}
                                                />
                                                <div>
                                                    <div className="kbola-check-text">{org.name}</div>
                                                    <div className="kbola-check-sub">
                                                        {isEnterpriseOrganization(org)
                                                            ? t("campaignForm.enterprisePlan", {}, "Enterprise plan")
                                                            : t("campaignForm.managedOrganization", {}, "Managed organization")}
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : managedOrganizationsLoading ? (
                            <div className="alert alert-light mb-0" style={{ fontSize: 12 }}>
                                {t("campaigns.loading")}
                            </div>
                        ) : null}
                        </CollapsibleCard>
                    ) : null}

                    {!isTextOnlyEdit ? (
                        <CollapsibleCard
                            scopeId={scopeId}
                            step={4}
                            title={t("campaignForm.scheduleBudget")}
                            open={openSec.schedule}
                            onToggle={() => toggleSec("schedule")}
                        >
                        <div className="form-row">
                            <div className="form-group col-12 col-md-6">
                                <label className="kbola-label">
                                    {t("campaignForm.startDate")} <span className="kbola-required">*</span>
                                </label>
                                <input
                                    className="form-control kbola-input"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>

                            <div className="form-group col-12 col-md-6">
                                <label className="kbola-label">
                                    {t("campaignForm.endDate")} <span className="kbola-required">*</span>
                                </label>
                                <input
                                    className="form-control kbola-input"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        {isInternalCampaign ? (
                            <div className="alert alert-success mb-3" style={{ fontSize: 12 }}>
                                {t(
                                    "campaignForm.internalCampaignIncluded",
                                    {},
                                    "Internal AngeonX Sports campaigns do not require Stripe payment."
                                )}
                            </div>
                        ) : isOrganizationCampaign ? (
                            <div className="alert alert-success mb-3" style={{ fontSize: 12 }}>
                                {t(
                                    "campaignForm.enterpriseCampaignIncluded",
                                    {},
                                    "Organization-targeted campaigns are included with the Enterprise plan. No Stripe payment is required."
                                )}
                            </div>
                        ) : isMarketplacePromotionIncluded ? (
                            <div className="alert alert-success mb-3" style={{ fontSize: 12 }}>
                                {t(
                                    "campaignForm.enterpriseMarketplaceIncluded",
                                    {},
                                    "This marketplace promotion is included with your Enterprise league subscription. No Stripe payment is required."
                                )}
                            </div>
                        ) : hasMissingRates ? (
                            <div className="alert alert-warning mb-3" style={{ fontSize: 12 }}>
                                {t("campaignForm.missingRatesFor")} {effectiveCurrency}: {missingRateText}
                            </div>
                        ) : null}

                        <div
                            className="mt-3"
                            style={{
                                backgroundColor: colors.surface,
                                color: colors.textOnSurface,
                                borderRadius: 14,
                                padding: "12px 14px",
                                border: `1px solid ${primaryBorder}`,
                                boxShadow: "0 18px 36px rgba(0,0,0,0.12)",
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div style={{ fontWeight: 900 }}>{t("campaignForm.estimatedTotal")}</div>
                                    <div style={{ fontSize: 12, color: colors.textMuted }}>
                                        {isInternalCampaign
                                            ? t("campaignForm.internalIncludedShort", {}, "Internal campaign")
                                            : isOrganizationCampaign
                                                ? t("campaignForm.enterpriseIncludedShort", {}, "Included with Enterprise")
                                                : isMarketplacePromotionIncluded
                                                    ? t("campaignForm.enterpriseMarketplaceIncludedShort", {}, "Included with Enterprise league")
                                            : `${t("campaignForm.basedOnPlacementsCountriesDuration")} (${pricing.days || 0} ${t("campaignForm.days")})`}
                                    </div>
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 900, color: colors.primary }}>
                                    ${estimatedTotal}{" "}
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: colors.textMuted,
                                        }}
                                    >
                                        {effectiveCurrency}
                                    </span>
                                </div>
                            </div>
                        </div>
                        </CollapsibleCard>
                    ) : null}

                    <CollapsibleCard
                        scopeId={scopeId}
                        step={5}
                        title={t("campaignForm.creative")}
                        open={openSec.creative}
                        onToggle={() => toggleSec("creative")}
                    >
                        <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
                            {t("campaignForm.creativeHint")}
                        </div>

                        {!isTextOnlyEdit ? (
                            <FilePicker
                                scopeId={scopeId}
                                colors={colors}
                                label={t("campaignForm.brandLogoSquare")}
                                hint={t("campaignForm.recommendedRatio")}
                                accept="image/*"
                                value={logoFile}
                                previewUrl={logoPreviewUrl ? normalizeUrl(logoPreviewUrl) : null}
                                onChangeFile={(file) => setLogoFile(file)}
                                onClear={clearLogo}
                            />
                        ) : null}

                        <Field label={t("campaignForm.headline")} required>
                            <input
                                className="form-control kbola-input"
                                placeholder={t("campaignForm.headlinePlaceholder")}
                                value={headline}
                                onChange={(e) => setHeadline(e.target.value)}
                            />
                        </Field>

                        <Field label={t("campaignForm.bodyPitch")} required>
                            <textarea
                                className="form-control kbola-input"
                                rows={3}
                                placeholder={t("campaignForm.bodyPitchPlaceholder")}
                                value={bodyText}
                                onChange={(e) => setBodyText(e.target.value)}
                            />
                        </Field>

                        <div className="form-row mb-0">
                            <div className="form-group col-12 col-md-6">
                                <label className="kbola-label">
                                    {t("campaignForm.ctaButtonLabel")} <span className="kbola-required">*</span>
                                </label>
                                <input
                                    className="form-control kbola-input"
                                    placeholder={t("campaignForm.ctaButtonLabelPlaceholder")}
                                    value={ctaLabel}
                                    onChange={(e) => setCtaLabel(e.target.value)}
                                />
                            </div>

                            <div className="form-group col-12 col-md-6">
                                <label className="kbola-label">
                                    {t("campaignForm.ctaUrl")} <span className="kbola-required">*</span>
                                </label>
                                <input
                                    className="form-control kbola-input"
                                    placeholder={t("campaignForm.ctaUrlPlaceholder")}
                                    value={ctaUrl}
                                    onChange={(e) => setCtaUrl(e.target.value)}
                                />
                            </div>
                        </div>
                    </CollapsibleCard>
                </div>

                {/* RIGHT */}
                <div className="col-12 col-lg-4 mb-5">
                    <div className="card kbola-card">
                        <div className="kbola-card-header">
                            <h3 className="kbola-card-title">{t("campaignForm.campaignSummary")}</h3>
                        </div>

                        <div className="card-body kbola-card-body">
                            <div style={{ fontSize: 13, color: colors.woodsmoke }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.name")}</strong>
                                    <span>{campaignName || "—"}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.sponsor")}</strong>
                                    <span>{selectedSponsorName}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.currency")}</strong>
                                    <span>{effectiveCurrency}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.placements")}</strong>
                                    <span>{zones.length ? `${zones.length} ${t("campaignForm.selected")}` : "—"}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.countries")}</strong>
                                    <span>{countries?.length ? countries.join(", ") : "—"}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.platforms")}</strong>
                                    <span>
                                        {platforms?.length
                                            ? platforms
                                                .map((platform) =>
                                                    t(
                                                        `campaignForm.platformOptions.${platform}`,
                                                        {},
                                                        platform
                                                    )
                                                )
                                                .join(", ")
                                            : "—"}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.sports")}</strong>
                                    <span>{sports.length ? sports.map(getSportLabel).join(", ") : t("campaignForm.all")}</span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignForm.type", {}, "Type")}</strong>
                                    <span>
                                        {isInternalCampaign
                                            ? t("campaignForm.internalCampaign", {}, "Internal AngeonX Sports campaign")
                                            : isOrganizationCampaign
                                                ? t("campaignForm.organizationCampaign", {}, "Organization campaign")
                                                : isMarketplacePromotionIncluded
                                                    ? t("campaignForm.marketplacePromotion", {}, "Marketplace promotion")
                                                : t("campaignForm.globalSponsorCampaign", {}, "Global sponsor campaign")}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                    <strong>{t("campaignReview.organizations", {}, "Organizations")}</strong>
                                    <span>
                                        {isInternalCampaign
                                            ? t("campaignForm.allUsers", {}, "All users")
                                            : targetOrganizationIds.length
                                            ? targetOrganizationIds
                                                .map((id) => targetableOrganizations.find((org) => String(org.id) === String(id))?.name)
                                                .filter(Boolean)
                                                .join(", ")
                                            : t("campaignForm.global", {}, "Global")}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between">
                                    <strong>{t("campaignForm.dates")}</strong>
                                    <span>
                                        {startDate || "?"} → {endDate || "?"}
                                    </span>
                                </div>

                                <hr style={{ borderTop: `1px solid ${surfaceBorder}` }} />

                                <div>
                                    <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 900 }}>
                                        {t("campaignForm.estimatedTotalShort")}
                                    </div>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: colors.woodsmoke }}>
                                        ${estimatedTotal}{" "}
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: colors.secondary,
                                                fontWeight: 800,
                                            }}
                                        >
                                            {effectiveCurrency}
                                        </span>
                                    </div>
                                </div>

                                {isInternalCampaign ? (
                                    <small
                                        className="d-block mt-2"
                                        style={{ fontSize: 12, color: "#198754", fontWeight: 700 }}
                                    >
                                        {t("campaignForm.internalCampaignIncluded", {}, "Internal AngeonX Sports campaigns do not require Stripe payment.")}
                                    </small>
                                ) : isOrganizationCampaign ? (
                                    <small
                                        className="d-block mt-2"
                                        style={{ fontSize: 12, color: "#198754", fontWeight: 700 }}
                                    >
                                        {t("campaignForm.enterpriseCampaignIncluded", {}, "Organization-targeted campaigns are included with the Enterprise plan. No Stripe payment is required.")}
                                    </small>
                                ) : isMarketplacePromotionIncluded ? (
                                    <small
                                        className="d-block mt-2"
                                        style={{ fontSize: 12, color: "#198754", fontWeight: 700 }}
                                    >
                                        {t("campaignForm.enterpriseMarketplaceIncluded", {}, "This marketplace promotion is included with your Enterprise league subscription. No Stripe payment is required.")}
                                    </small>
                                ) : hasMissingRates ? (
                                    <small
                                        className="d-block mt-2"
                                        style={{ fontSize: 12, color: "#a66b00", fontWeight: 700 }}
                                    >
                                        {t("campaignForm.missingRatesLabel")}: {missingRateText}
                                    </small>
                                ) : (
                                    <small
                                        className="d-block mt-2"
                                        style={{ fontSize: 12, color: colors.textMuted }}
                                    >
                                        {t("campaignForm.finalPriceHint")}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Live previews */}
                    <div className="col-md-12 text-center mt-4 mt-md-0">
                        <div
                            style={{
                                borderRadius: 10,
                                width: 56,
                                height: 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 900,
                                color: colors.woodsmoke,
                                fontSize: "1.2rem",
                                lineHeight: 1.1,
                                textAlign: "center",
                            }}
                        >
                            {t("campaignForm.preview")}
                        </div>

                        <div
                            style={{
                                backgroundColor: colors.shark,
                                border: `1px solid ${surfaceBorder}`,
                                borderRadius: 16,
                                padding: 16,
                                maxWidth: 320,
                                margin: "0 auto",
                                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                                textAlign: "left",
                                fontSize: "0.8rem",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    gap: 12,
                                }}
                            >
                                {logoSrc ? (
                                    <img
                                        src={logoSrc}
                                        alt={t("campaignForm.logoPreview")}
                                        style={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 10,
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            backgroundColor: colors.canary,
                                            borderRadius: 10,
                                            width: 56,
                                            height: 56,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 900,
                                            color: colors.woodsmoke,
                                            fontSize: "0.75rem",
                                            lineHeight: 1.1,
                                            textAlign: "center",
                                        }}
                                    >
                                        {t("campaignForm.yourLogoFirst")}
                                        <br />
                                        {t("campaignForm.yourLogoSecond")}
                                    </div>
                                )}

                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            color: colors.white,
                                            fontWeight: 800,
                                            lineHeight: 1.3,
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        {headline || t("campaignForm.yourBusinessCardTitle")}
                                    </div>
                                    <div
                                        style={{
                                            color: colors.manatee,
                                            fontSize: "0.8rem",
                                            lineHeight: 1.4,
                                            marginTop: 4,
                                        }}
                                    >
                                        {bodyText || t("campaignForm.trainWithBestCoaches")}
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 6,
                                            fontSize: "0.75rem",
                                            textDecoration: "underline",
                                            color: colors.white,
                                            fontWeight: 700,
                                            textAlign: "right",
                                        }}
                                    >
                                        {t("campaignForm.viewMore")}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        color: colors.manatee,
                                        fontSize: "0.85rem",
                                        fontWeight: 700,
                                    }}
                                >
                                    ✕
                                </div>
                            </div>

                            <div style={{ marginTop: 16 }}>
                                <div
                                    style={{
                                        backgroundColor: colors.canary,
                                        color: colors.woodsmoke,
                                        fontWeight: 900,
                                        width: "100%",
                                        borderRadius: 10,
                                        padding: "12px 16px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    <span>{ctaLabel || t("campaignForm.seeMore")}</span>
                                    <span style={{ fontWeight: 900 }}>›</span>
                                </div>
                            </div>

                            <div
                                style={{
                                    fontSize: "0.7rem",
                                    color: colors.manatee,
                                    marginTop: 10,
                                }}
                            >
                                {t("campaignForm.sponsoredPlacement")}
                            </div>
                        </div>

                        <div
                            style={{
                                backgroundColor: colors.shark,
                                border: `1px solid ${surfaceBorder}`,
                                borderRadius: 16,
                                padding: 16,
                                maxWidth: 320,
                                margin: "24px auto 0 auto",
                                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                                textAlign: "left",
                                fontSize: "0.8rem",
                            }}
                        >
                            {logoSrc ? (
                                <img
                                    src={logoSrc}
                                    alt={t("campaignForm.logo")}
                                    style={{
                                        width: "100%",
                                        borderRadius: 12,
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        backgroundColor: colors.canary,
                                        color: colors.woodsmoke,
                                        borderRadius: 12,
                                        minHeight: 120,
                                        fontWeight: 900,
                                        fontSize: "0.9rem",
                                        lineHeight: 1.4,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        padding: 12,
                                    }}
                                >
                                    {t("campaignForm.fullScreenHighlight")}
                                    <br />
                                    {t("campaignForm.yourBrandFeature")}
                                </div>
                            )}

                            <div style={{ marginTop: 16, color: colors.white }}>
                                <div
                                    style={{
                                        fontWeight: 900,
                                        fontSize: "0.9rem",
                                        lineHeight: 1.4,
                                        marginBottom: 8,
                                    }}
                                >
                                    {headline || t("campaignForm.yourHeadline")}
                                </div>
                                <div
                                    style={{
                                        color: colors.manatee,
                                        fontSize: "0.8rem",
                                        lineHeight: 1.5,
                                        minHeight: 150,
                                    }}
                                >
                                    {bodyText || t("campaignForm.fullStoryText")}
                                </div>
                            </div>

                            <div style={{ marginTop: 16 }}>
                                <div
                                    style={{
                                        backgroundColor: colors.canary,
                                        color: colors.woodsmoke,
                                        fontWeight: 900,
                                        width: "100%",
                                        borderRadius: 10,
                                        padding: "12px 16px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    <span>{ctaLabel || t("campaignForm.seeMore")}</span>
                                    <span style={{ fontWeight: 900 }}>›</span>
                                </div>
                            </div>

                            <div
                                style={{
                                    fontSize: "0.7rem",
                                    color: colors.manatee,
                                    marginTop: 10,
                                }}
                            >
                                {t("campaignForm.exampleExpandedView")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky footer action bar */}
            <div
                className="border-top p-3"
                style={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999,
                    backgroundColor: colors.surface,
                    borderTop: `1px solid ${primaryBorder}`,
                }}
            >
                <div className="container-fluid d-flex flex-column flex-md-row justify-content-between align-items-center">
                    <div
                        className="mb-2 mb-md-0"
                        style={{ fontSize: "0.8rem", color: colors.textMuted }}
                    >
                        {isTextOnlyEdit
                            ? t("campaignForm.editingCampaignText", {}, "Editing campaign text only...")
                            : mode === "edit"
                                ? t("campaignForm.editingDraft")
                                : t("campaignForm.unsavedChanges")}
                    </div>

                    <div>
                        <button
                            className="btn btn-secondary mr-2"
                            onClick={() => (onCancel ? onCancel() : window.history.back())}
                        >
                            {t("campaignForm.cancel")}
                        </button>

                        {mode === "create" ? (
                            <button
                                className="btn btn-outline-warning mr-2"
                                onClick={handleCreateDraft}
                                disabled={submitDisabled}
                            >
                                {creating ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("campaignForm.saving")}
                                    </>
                                ) : (
                                    t("campaignForm.saveDraft")
                                )}
                            </button>
                        ) : (
                            <button
                                className="btn btn-outline-warning mr-2"
                                onClick={handleUpdateDraft}
                                disabled={submitDisabled}
                            >
                                {updating || updatingText ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("campaignForm.saving")}
                                    </>
                                ) : (
                                    t("campaignForm.saveChanges")
                                )}
                            </button>
                        )}

                        {!isTextOnlyEdit ? (
                            <button
                                className="btn btn-kbola-canary"
                                onClick={saveAndGoToReview}
                                disabled={!isValid() || creating || updating}
                            >
                                {t("campaignForm.reviewConfirm")} <i className="fas fa-arrow-right ml-2" />
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
